// The row field selection shared by the classic subscription and the
// partition-pruned gradual query, so both return the same record_log shape.
const recordLogRowFields = `
    created_at
    data
    id
    record_id
    record_id_record {
        id
        name
        record_type
    }
    record_log_record_plain_list_record_log_id(limit:2, order:"+id",offset:0) {
        reference_record_by
        reference_record_select
        reference_record_type
    }
    subject
    level
`;

export const recordLogListSubscription = `
    subscription record_log_list($filter: String, $limit: Int, $offset: Int, $order: String) {
        record_log_list(filter: $filter, limit: $limit, offset: $offset, order: $order) {
            record_log_count
            record_log_list {${recordLogRowFields}}
        }
    }
`;

// Partition-pruned gradual log read in COUNT mode: the consumer asks for up to
// `count` rows and the backend walks daily partitions server-side (newest-first,
// keyset-paged) until it has `count` rows, reaches the record/caller created_at
// floor (end of logs), or trips a safety cap — returning the rows plus a
// `continue_before` cursor and a `capped` flag in ONE round-trip. It never
// computes a total COUNT(*), so it never full-scans every retained daily
// partition. Load the next (older) screenful by re-issuing with the returned
// `continue_before` cursor until `more_available` is false. `limit` is the
// per-window SQL page ceiling the server pages each partition in. See the backend
// `record_log_list_gradual` field (cloudomation/resources/record_log.py, count
// mode) and operator-task-fe-logs-tab-bounded-dom-virtualization.
export const recordLogListGradualQuery = `
    query record_log_list_gradual($record_id: String, $container_id: String, $filter: String, $limit: Int, $count: Int, $continue_before: record_log_gradual_cursor_input) {
        record_log_list_gradual(record_id: $record_id, container_id: $container_id, filter: $filter, limit: $limit, count: $count, continue_before: $continue_before) {
            more_available
            scanned_from
            scanned_through
            capped
            continue_before {
                created_at
                id
            }
            record_log_list {${recordLogRowFields}}
        }
    }
`;

// Forward ("newer") gradual read: rows strictly NEWER than the `continue_after`
// keyset cursor, walking day windows toward the newest log in count mode. Rows
// come back newest-first; the envelope's `continue_after` is the cursor for the
// next forward page and is null once the newest row has been reached
// (`more_available` false). The Logs tab uses it to close the gap between the
// loaded top row and the true newest row before it opens the live subscription
// (operator-task-fe-logs-tab-load-newer-to-newest-before-subscribe).
export const recordLogListGradualNewerQuery = `
    query record_log_list_gradual($record_id: String, $filter: String, $limit: Int, $count: Int, $continue_after: record_log_gradual_cursor_input) {
        record_log_list_gradual(record_id: $record_id, filter: $filter, limit: $limit, count: $count, continue_after: $continue_after) {
            more_available
            capped
            continue_after {
                created_at
                id
            }
            record_log_list {${recordLogRowFields}}
        }
    }
`;

// Live row streaming for the Logs tab (operator-task-fe-logs-tab-live-row-updates).
// Deliberately the `record_log_plain_list` subscription, NOT the classic
// `record_log_list` one above: the plain variant resolves with plain=True so it
// returns a bare row array with NO record_log_count, avoiding the unconditional
// full-partition COUNT(*) the classic subscription runs on every record_log
// NOTIFY (the exact cost the gradual rework removed). It is driven by the same
// `record_log` LISTEN/NOTIFY channel, so it re-pushes the newest window whenever
// a matching row is written. The caller floors `filter` at the current UTC day
// and caps `limit` (LIVE_WINDOW_LIMIT) so each push stays cheap and partition-
// bounded, then MERGES the arrivals into the accumulated gradual list.
export const recordLogPlainListSubscription = `
    subscription record_log_plain_list($filter: String, $limit: Int, $offset: Int, $order: String) {
        record_log_plain_list(filter: $filter, limit: $limit, offset: $offset, order: $order) {${recordLogRowFields}}
    }
`;

// The OLDEST rows for "Go to oldest" on a record- or container-scoped Logs tab:
// the gradual read with `from_oldest`, which walks forward from the scope floor
// (the record's / container's creation) and returns the oldest `count` rows
// newest-first, plus a `continue_after` cursor toward newer rows.
export const recordLogListGradualOldestQuery = `
    query record_log_list_gradual($record_id: String, $container_id: String, $filter: String, $limit: Int, $count: Int) {
        record_log_list_gradual(record_id: $record_id, container_id: $container_id, filter: $filter, limit: $limit, count: $count, from_oldest: true) {
            more_available
            capped
            continue_after {
                created_at
                id
            }
            record_log_list {${recordLogRowFields}}
        }
    }
`;

// Fallback for "Go to oldest" on an UNSCOPED Logs view (workspace-wide, bundle
// manager), which has no floor to start from: a plain ascending list read (no COUNT; the
// created_at / record_id indexes make it cheap on the oldest partition). The
// caller passes order "+created_at,+id" and reverses the rows to newest-first.
export const recordLogOldestQuery = `
    query record_log_plain_list($filter: String, $limit: Int, $offset: Int, $order: String) {
        record_log_plain_list(filter: $filter, limit: $limit, offset: $offset, order: $order) {${recordLogRowFields}}
    }
`;

export const clearLogsMutation = (type) => `mutation ($id: String!) {
  clear_${type}_logs(id: $id)}`;
